import React, { useState, useEffect } from 'react';
import { getUsers, createUser, deleteUser, changeUserRole, getOrgEvents } from '../../api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/Modal';
import Alert from '../../components/Alert';
import Loading from '../../components/Loading';
import {
  PlusIcon,
  UserGroupIcon,
  TrashIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

export default function MembersPage() {
  const { user, organizations } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ username: '', password: '', displayName: '', role: 'member' });
  // Map: userId -> { attended, total } for this month
  const [attendance, setAttendance] = useState({});

  useEffect(() => { loadUsers(); }, []);

  // Load attendance for this month from owner's org events
  useEffect(() => {
    const myOrg = organizations?.find(o => o.ownerId === user?._id);
    if (!myOrg) return;
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `${y}-${m}`;
    getOrgEvents(myOrg._id).then(res => {
      const monthEvents = res.data.filter(e => e.date?.startsWith(prefix));
      const total = monthEvents.length;
      const map = { _total: total };
      for (const ev of monthEvents) {
        for (const rsvp of (ev.attending || [])) {
          if (!map[rsvp.userId]) map[rsvp.userId] = { attended: 0, total };
          map[rsvp.userId].attended++;
        }
      }
      setAttendance(map);
    }).catch(() => {});
  }, [organizations, user]);

  const loadUsers = async () => {
    try {
      const res = await getUsers();
      const allUsers = Array.isArray(res.data) ? res.data : (res.data.items || []);
      const others = allUsers.filter(u => u._id !== user._id && u.role !== 'admin' && u.role !== 'owner');
      others.sort((a, b) => (a.role === 'leader' ? -1 : b.role === 'leader' ? 1 : 0));
      // Always prepend the logged-in owner themselves (backend doesn't include them in the response)
      setMembers([user, ...others]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createUser({ ...form });
      await loadUsers();
      setShowCreate(false);
      setForm({ username: '', password: '', displayName: '', role: 'member' });
      setSuccess(`${form.role === 'leader' ? 'Leader' : 'Member'} created successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await deleteUser(userId);
      await loadUsers();
      setSuccess('User deleted!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await changeUserRole(userId, newRole);
      await loadUsers();
      setSuccess('Role updated!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change role');
    }
  };

  if (loading) return <Loading />;

  const myOrg = organizations?.find(o => o.ownerId === user?._id);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Members</h2>
          <div className="subtitle">Manage club members and leaders</div>
        </div>
        <button className="btn btn-primary" onClick={() => myOrg ? setShowCreate(true) : null} disabled={!myOrg} title={!myOrg ? 'Create an organization first' : undefined}>
          <PlusIcon className="icon" /> Create User
        </button>
      </div>
      {!myOrg && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: 'var(--warning-light, #fef3c7)', border: '1px solid var(--warning, #f59e0b)', borderRadius: 'var(--radius)', fontSize: 13, color: 'var(--warning, #92400e)' }}>
          You need to create an organization before you can add members.
        </div>
      )}

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="members-list">
        {members.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <UserGroupIcon style={{ width: 48, height: 48, opacity: 0.5 }} />
            <p style={{ marginTop: 12 }}>No members yet. Create your first member!</p>
          </div>
        )}
        {members.map(u => (
          <div key={u._id} className="member-item">
            <div className="member-info">
              <div className="member-avatar" style={{
                background: u._id === user._id ? 'var(--accent)' : u.role === 'leader' ? 'var(--info)' : 'var(--success)'
              }}>
                {u.avatar
                  ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : u.displayName?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div className="member-name">{u.displayName}</div>
                <div className="member-role">@{u.username}</div>
              </div>
              {(() => {
                const totalEvents = attendance._total ?? 0;
                if (totalEvents === 0) return null;
                const info = attendance[u._id];
                const attended = info?.attended ?? 0;
                const pct = Math.round((attended / totalEvents) * 100);
                const color = pct >= 70 ? 'var(--success)' : pct >= 40 ? 'var(--warning, #f59e0b)' : 'var(--danger)';
                return (
                  <div title={`Attended ${attended}/${totalEvents} events this month`} style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                    background: `${color}22`, color, border: `1px solid ${color}55`,
                    whiteSpace: 'nowrap', marginLeft: 6
                  }}>
                    {attended}/{totalEvents} this month
                  </div>
                );
              })()}
            </div>
            {u._id === user._id ? (
              <span className="badge badge-purple"><ShieldCheckIcon className="icon" /> Owner (You)</span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select
                  value={u.role}
                  onChange={(e) => handleRoleChange(u._id, e.target.value)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--border)',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                    cursor: 'pointer'
                  }}
                >
                  <option value="member">Member</option>
                  <option value="leader">Leader</option>
                </select>
                <span className={`badge ${u.role === 'leader' ? 'badge-info' : 'badge-success'}`}>
                  {u.role === 'leader' && <ShieldCheckIcon className="icon" />}
                  {u.role}
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(u._id)} style={{ color: 'var(--danger)' }}>
                  <TrashIcon className="icon" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create New User"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate}>Create</button>
          </>
        }
      >
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label>Username</label>
            <input className="form-input" value={form.username} onChange={e => setForm({...form, username: e.target.value.slice(0, 30)})} required maxLength={30} />
            <div style={{ fontSize: 11, color: form.username.length >= 30 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{form.username.length}/30 characters</div>
          </div>
          <div className="form-group">
            <label>Display Name</label>
            <input className="form-input" value={form.displayName} onChange={e => setForm({...form, displayName: e.target.value.slice(0, 30)})} required maxLength={30} />
            <div style={{ fontSize: 11, color: form.displayName.length >= 30 ? 'var(--danger)' : 'var(--text-muted)', marginTop: 4 }}>{form.displayName.length}/30 characters</div>
          </div>
          <div className="form-group">
            <label>Password</label>
            <input className="form-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required />
          </div>
          <div className="form-group">
            <label>Role</label>
            <select className="form-input" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
              <option value="member">Member</option>
              <option value="leader">Leader</option>
            </select>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--info-light)', borderRadius: 'var(--radius)' }}>
            {form.role === 'leader'
              ? <span><strong>Leaders</strong> have the same permissions as you — they can manage members, events, and the organization.</span>
              : <span><strong>Members</strong> can view events, provide feedback, and participate in the organization.</span>
            }
          </div>
        </form>
      </Modal>
    </div>
  );
}
